// Cross-package contracts for Ride N Repair OS — the ONE definition of every constant that the site, the API,
// leadgen and the database must agree on. Plain ESM + JSDoc so Node (api, leadgen) imports it natively and
// TypeScript (site, allowJs) infers exact literal types from the `@type {const}` casts.
//
// Pricing lives next door in pricing.json (data, amounts in cents). DB CHECK constraints mirror SEGMENTS and the
// bundle ids; `node api/scripts/check-contracts.mjs` fails if any of these drift.

/** Brand facts used in copy, emails, ICS files and SMS replies. */
export const BRAND = /** @type {const} */ ({
  name: 'Ride N Repair OS',
  shortName: 'RNR OS',
  company: 'Ride N Repair, Inc.',
  domain: 'ridenrepair.ai',
  url: 'https://ridenrepair.ai',
  email: { sales: 'sales@ridenrepair.ai', billing: 'billing@ridenrepair.ai', hello: 'ceo@ridenrepair.ai' },
  postalAddress: '131 Continental Dr, Suite 305, Newark, DE 19713, USA',
});

/** Customer segments. Mirrored by CHECK constraints on gtm.leads, gtm.demo_tokens, app.accounts, core.tenants. */
export const SEGMENTS = /** @type {const} */ (['mobile_mechanic', 'fleet', 'roadside', 'repair_shop', 'franchise', 'dealership']);
/**
 * Tenant types: the segments a tenant may have. Today every customer segment can become a tenant, so this is SEGMENTS
 * itself, not a second list (the CHECK on core.tenants.segment gains `dealership` in migration 242; 270 re-checks it).
 * Code that means "a kind of tenant" imports this name, so the two can diverge in one place if they ever must.
 */
export const TENANT_SEGMENTS = SEGMENTS;

/**
 * `dealership` means a franchised new-car store. A used-car lot is not one (it is sold the workshop price list), and
 * Google gives both the same `car_dealer` type, so the business name decides. One pattern for the crawler
 * (leadgen/lib/upsert.mjs) and the email classifier (outbound/src/features.js); compile with the `i` flag.
 */
export const USED_CAR_LOT_PATTERN = '\\b(used (cars?|autos?|trucks?|vehicles?)|pre-?owned|buy here,? pay here|bhph|(auto|car|motor|truck) sales|(auto|car) ?mart|auto brokers?|auto wholesale(rs?)?|wholesale (autos?|cars?|motors)|auto auction|auto outlet|car lot|carmax|carvana)\\b';

/** First-party analytics event whitelist (POST /v1/events). The API rejects any other type. */
export const EVENT_TYPES = /** @type {const} */ ([
  'page_view', 'token_visit', 'scroll', 'dwell', 'engage', 'cta_click', 'share_click',
  'demo_start', 'demo_progress', 'demo_complete', 'stage_open', 'sandbox_open',
  'autoplay_q1', 'autoplay_q2', 'autoplay_q3', 'autoplay_q4', 'autoplay_complete',
  'audio_play', 'sample_call_play', 'try_task',
  'pricing_view', 'pricing_calc_change', 'calendly_click', 'demo_gate_view', 'demo_gate_submit',
  'booking_view', 'booking_complete',
  'signup_start', 'checkout_start', 'checkout_complete', 'onboarding_step', 'go_live',
]);

/**
 * Website analytics (migration 288: the `analytics` schema; api/src/modules/web_analytics; site/src/lib/track.ts and
 * web-analytics.ts). The browser sends these in the same POST /v1/events batches as EVENT_TYPES. They are stored only
 * in the analytics tables, never in gtm.events. EVENT_TYPES are stored in both (in gtm.events as before), except
 * `gtmServerOnly`: POST /v1/booking writes booking_complete to gtm.events itself, so a browser copy goes to analytics only.
 * Nothing a visitor types is ever sent: form events carry the field NAME (one of `formFields`) and error codes only.
 */
export const WEB_EVENT_TYPES = /** @type {const} */ ([
  'page_end', 'section_view', 'scroll_depth', 'nav_click', 'faq_open', 'outbound_click', 'phone_click', 'email_click',
  'contact_copy', 'rage_click', 'form_view', 'form_start', 'form_field', 'form_error', 'slot_pick', 'form_submit',
  'booking_error',
]);
export const WEB_ANALYTICS = Object.freeze({
  gtmServerOnly: /** @type {const} */ (['booking_complete']),
  /** A cta_click's `kind` (data-cta-kind) → the interaction type stored. No kind = cta_click. */
  clickKinds: Object.freeze({ cta: 'cta_click', nav: 'nav_click', tab: 'tab_switch', login: 'login_click' }),
  /** Sent under one name, stored under another. */
  rename: Object.freeze({ pricing_calc_change: 'calculator_change' }),
  /** Lifecycle events: they shape sessions and page views and are not stored as interactions. */
  lifecycle: /** @type {const} */ (['page_view', 'page_end', 'token_visit']),
  /** URL parameters kept with a page view (and the session's landing): everything else in the query is dropped. */
  queryKeys: /** @type {const} */ (['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'utm_adset', 'utm_id', 'utm_placement',
    'gclid', 'gbraid', 'wbraid', 'fbclid', 'li_fat_id', 'msclkid', 'v', 's', 'token', 'm']),
  /** Booking form field names (form_field / form_error). Values are never sent. */
  formFields: /** @type {const} */ (['name', 'email', 'company', 'phone', 'techs', 'software', 'rooftops', 'scheduler', 'role', 'notes', 'sms', 'slot',
    'business_type', 'guest', 'first_name', 'last_name', 'website']),
  /** Keys an interaction may keep in `value` (numbers, booleans, short strings without an @ or a run of digits). */
  valueKeys: /** @type {const} */ (['dest', 'kind', 'pct', 'idx', 'code', 'status', 'form', 'topic', 'day_offset', 'bucket',
    'seconds', 'ms_since_load', 'calc', 'plan', 'techs', 'stores', 'what', 'attempt', 'n', 'variant']),
  scrollMilestones: /** @type {const} */ ([25, 50, 75, 100]),
  maxTarget: 120,
  maxSection: 40,
  maxValueBytes: 1024,
  maxTitle: 200,
  /** A session with no event for this long is classified (the after-the-fact bot rules) by the worker. */
  sessionIdleMinutes: 30,
  /** analytics.interactions older than this are removed by the worker. */
  retentionMonths: 13,
});

/** Identifier formats shared by the browser, the API and gtm.demo_tokens (CHECK). */
export const TOKEN_PATTERN = '^[A-Za-z0-9]{8,32}$';
export const ANON_PATTERN = '^[A-Za-z0-9_-]{8,64}$';

/**
 * Ad attribution (site/src/lib/track.ts builds it; api/src/attribution.js validates it; gtm.conversions stores it).
 * A touch is { landing, ref?, utm?, click?, ts }: `click` holds the ad click identifiers found on the landing URL.
 * First touch (rnr_ft) is stored once per browser; last paid touch (rnr_lt) is overwritten by any landing that carries
 * a click identifier or a paid utm_medium, and is dropped after lastTouchDays.
 */
export const ATTRIBUTION = Object.freeze({
  clickKeys: /** @type {const} */ (['gclid', 'gbraid', 'wbraid', 'fbclid', 'li_fat_id', 'msclkid']),
  /** The utm keys a touch keeps (the site sends them, the API stores them). Unknown keys are dropped by the API, never refused,
   *  so a new key ships API-first: the site starts sending it in a later release. With the Meta url_tags, utm_campaign /
   *  utm_term / utm_content carry the campaign / ad set / ad ids and utm_id the campaign id; utm_adset is the older
   *  name-based tag. */
  utmKeys: /** @type {const} */ (['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_adset', 'utm_id', 'utm_placement']),
  paidMedia: /** @type {const} */ (['cpc', 'ppc', 'paid', 'paid_social', 'paid-social', 'paidsocial', 'paid_search', 'display']),
  clickIdPattern: '^[A-Za-z0-9._~-]+$',
  maxClickId: 512,
  maxUtm: 200,
  maxPath: 300,
  lastTouchDays: 90,
});

const UTM_ENCODED = /%[0-9A-Fa-f]{2}|\+/;
const UTM_SPACE = /[ \t\n\r\f\v]+/g;
/**
 * One utm value as stored: up to two rounds of ('+' → space, URL-decode) while it still looks encoded and decoding changes
 * it (Meta's placement macros arrive double-encoded: 10%252F29%2BRNR → 10/29 RNR); runs of whitespace become one space,
 * trimmed, at most ATTRIBUTION.maxUtm characters; utm_source and utm_medium lower-cased, every other value keeps its case.
 * A round that cannot be decoded (a lone %, a broken UTF-8 sequence) stops the decoding and keeps the value as it was.
 * The SQL twin is analytics.normalize_utm (migration 380); both run the same fixtures (shared/tests/utm.test.mjs).
 * @param {string} key @param {unknown} v @returns {string}
 */
export function normalizeUtm(key, v) {
  let s = typeof v === 'string' ? v : '';
  for (let i = 0; i < 2 && UTM_ENCODED.test(s); i += 1) {
    let d;
    try {
      d = decodeURIComponent(s.replace(/\+/g, ' '));
    } catch {
      break;
    }
    if (d.includes('\u0000') || d === s) break;
    s = d;
  }
  s = Array.from(s.replace(UTM_SPACE, ' ').replace(/^ | $/g, '')).slice(0, ATTRIBUTION.maxUtm).join('');
  return key === 'utm_source' || key === 'utm_medium' ? s.toLowerCase() : s;
}

/**
 * SMS opt-in wording (10DLC / toll-free). Shown next to the optional checkbox and stored verbatim with each
 * consent record. Change the text → bump the version.
 */
export const SMS_CONSENT = /** @type {const} */ ({
  version: '2026-09-27.1',
  text:
    'I agree to receive text messages from Ride N Repair OS about my demo, account and appointments at the number provided. ' +
    'Message frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of purchase.',
});

/**
 * The call a prospect books (site /book, the desk's "Book a demo call"): the free consultation, a Google Meet video
 * call. `minutes` is the slot length the site and the desk say; the slots themselves come from
 * app.availability.slot_minutes (migration 379 sets it to this), and every email states each booking's own length.
 */
export const DEMO_CALL = /** @type {const} */ ({ minutes: 30 });
/**
 * The recording line in every demo invite from meet@'s calendar (growth OS DEMO-01; owner-approved wording, 1 Nov 2026).
 * The invite, its .ics and the Google event carry it once the booking has its own Meet room (booking-mail.js).
 */
export const RECORDING_LINE = "We record and transcribe our calls so nothing you tell us gets lost. If you'd rather we didn't, just say so at the start.";

/** Hosts that carry many businesses' pages: such a host names no one store, so it is never matched as a store's domain. */
export const SHARED_HOSTS = /** @type {const} */ ([
  'facebook.com', 'm.facebook.com', 'instagram.com', 'google.com', 'sites.google.com', 'business.site', 'g.page', 'yelp.com',
  'linktr.ee', 'wixsite.com', 'squarespace.com', 'godaddysites.com', 'weebly.com', 'linkedin.com', 'cars.com', 'dealerrater.com',
]);

/**
 * A store's website as someone typed it (smithtoyota.com, www.smithtoyota.com/service, https://Smithtoyota.com/) →
 *   { website: 'https://smithtoyota.com', host: 'smithtoyota.com', name: 'Smithtoyota.com' }
 * or null when it is not a web address. The one rule for the booking page (it validates as you type) and the API (it
 * stores `website` and names the store `name` when no store name was sent). On a shared host (SHARED_HOSTS) the page's
 * path is the store's, so it is kept: facebook.com/smithtoyota → website https://facebook.com/smithtoyota, name
 * 'Smithtoyota'.
 * @param {unknown} raw
 * @returns {{ website: string, host: string, name: string } | null}
 */
export function storeSite(raw) {
  const s = String(raw ?? '').trim();
  if (!s || s.length > 300 || /\s/.test(s)) return null;
  let u;
  try {
    u = new URL(/^https?:\/\//i.test(s) ? s : `https://${s}`);
  } catch {
    return null;
  }
  if (u.username || u.password) return null;
  const host = u.hostname.toLowerCase().replace(/\.$/, '').replace(/^www\./, '');
  if (!/^(?=.{4,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,24}|xn--[a-z0-9-]{1,59})$/.test(host)) return null;
  const cap = (x) => x.charAt(0).toUpperCase() + x.slice(1);
  if (SHARED_HOSTS.includes(/** @type {any} */ (host))) {
    let seg = u.pathname.split('/').filter(Boolean)[0] || '';
    try {
      seg = decodeURIComponent(seg);
    } catch {
      /* kept as typed */
    }
    seg = seg.replace(/[^\p{L}\p{N}._-]/gu, '').slice(0, 100);
    if (seg) return { website: `https://${host}/${seg}`, host, name: cap(seg) };
  }
  return { website: `https://${host}`, host, name: cap(host) };
}

/** Signup deep link: /signup?plan=<bundle slug>[&techs=N][&locations=N]. The only supported query shape. */
export const SIGNUP_PARAMS = /** @type {const} */ ({ plan: 'plan', techs: 'techs', locations: 'locations', lines: 'lines', seats: 'seats', fleet_clients: 'fleet_clients', term: 'term', cancelled: 'cancelled' });

/**
 * @param {string} slug  bundle slug, e.g. "mobile-ops"
 * @param {{ techs?: number, locations?: number, lines?: number, seats?: number, fleet_clients?: number, term?: 'annual'|'quarterly'|'monthly', cancelled?: boolean }} [opts]
 * @returns {string}
 */
export function signupPath(slug, opts = {}) {
  const q = new URLSearchParams({ [SIGNUP_PARAMS.plan]: slug });
  if (opts.techs) q.set(SIGNUP_PARAMS.techs, String(opts.techs));
  if (opts.locations) q.set(SIGNUP_PARAMS.locations, String(opts.locations));
  if (opts.lines) q.set(SIGNUP_PARAMS.lines, String(opts.lines));
  if (opts.seats) q.set(SIGNUP_PARAMS.seats, String(opts.seats));
  if (opts.fleet_clients) q.set(SIGNUP_PARAMS.fleet_clients, String(opts.fleet_clients));
  if (opts.term) q.set(SIGNUP_PARAMS.term, String(opts.term));
  if (opts.cancelled) q.set(SIGNUP_PARAMS.cancelled, '1');
  return `/signup?${q}`;
}

/**
 * Consumer webmail domains. An opt-out from one of these never suppresses the whole domain, and the outbound engine
 * caps their share of a day's sends (13_EMAIL_CAMPAIGN_STRATEGY §4.3).
 */
export const FREEMAIL_DOMAINS = /** @type {const} */ ([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'ymail.com', 'aol.com', 'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
  'icloud.com', 'me.com', 'mac.com', 'comcast.net', 'att.net', 'sbcglobal.net', 'verizon.net', 'bellsouth.net', 'cox.net',
  'charter.net', 'earthlink.net', 'protonmail.com', 'proton.me', 'mail.com', 'gmx.com', 'zoho.com',
]);

/** Throwaway-inbox services. Lower case; a subdomain of one counts too (isDisposableEmail). */
export const DISPOSABLE_EMAIL_DOMAINS = Object.freeze([
  'mailinator.com',
  'guerrillamail.com',
  'guerrillamail.net',
  'guerrillamail.org',
  'sharklasers.com',
  'grr.la',
  '10minutemail.com',
  '10minutemail.net',
  'tempmail.com',
  'temp-mail.org',
  'temp-mail.io',
  'tempmailo.com',
  'tempail.com',
  'throwawaymail.com',
  'yopmail.com',
  'yopmail.net',
  'getnada.com',
  'nada.email',
  'trashmail.com',
  'trashmail.de',
  'dispostable.com',
  'maildrop.cc',
  'mailnesia.com',
  'mintemail.com',
  'fakeinbox.com',
  'emailondeck.com',
  'mohmal.com',
  'burnermail.io',
  'spamgourmet.com',
  'mailcatch.com',
  'moakt.com',
  'tmail.ws',
  'tmpmail.org',
  'tmpmail.net',
  'mail.tm',
  'mail.gw',
  'discard.email',
  'inboxkitten.com',
  'mytemp.email',
  'harakirimail.com',
  'spambox.us',
  'byom.de',
  'mvrht.net',
  'dropmail.me',
  'emailfake.com',
  'fakemail.net',
  'guerrillamailblock.com',
  'pokemail.net',
]);
const DISPOSABLE_SET = new Set(DISPOSABLE_EMAIL_DOMAINS);
/** An address at a throwaway-inbox service (or a subdomain of one). The booking refuses it; the classifier calls it spam. */
export function isDisposableEmail(email) {
  const parts = (String(email ?? '').trim().toLowerCase().split('@').pop() || '').split('.');
  for (let i = 0; i < parts.length - 1; i += 1) if (DISPOSABLE_SET.has(parts.slice(i).join('.'))) return true;
  return false;
}

/**
 * AI receptionist call disclosure (P4, fixed; tenants can't skip or edit it). Played before recording starts; the
 * version is stored on every call. Change the text → bump the version.
 */
export const VOICE_DISCLOSURE = /** @type {const} */ ({
  version: '2026-09-27.1',
  text: "Thanks for calling {business}. I'm an AI assistant and this call is recorded.",
});

/**
 * Carrier call-forwarding codes shown in onboarding (P4). `{number}` = the tenant's AI number as 10 digits (national10)
 * or 1 + 10 digits (digits11). `recommended` = conditional forwarding, so the owner's phone still rings first.
 */
export const CALL_FORWARDING = /** @type {const} */ ({
  version: '2026-09-27.1',
  carriers: {
    att: { label: 'AT&T', kind: 'gsm', number_format: 'digits11', recommended: 'no_answer',
      all: { on: '**21*{number}#', off: '##21#' }, no_answer: { on: '**61*{number}#', off: '##61#' },
      busy: { on: '**67*{number}#', off: '##67#' }, unreachable: { on: '**62*{number}#', off: '##62#' }, cancel_all: '##002#' },
    verizon: { label: 'Verizon', kind: 'cdma_style', number_format: 'national10', recommended: 'no_answer',
      all: { on: '*72{number}', off: '*73' }, no_answer: { on: '*71{number}', off: '*73' }, busy: { on: '*71{number}', off: '*73' },
      cancel_all: '*73' },
    tmobile: { label: 'T-Mobile', kind: 'gsm', number_format: 'digits11', recommended: 'no_answer',
      all: { on: '**21*{number}#', off: '##21#' }, no_answer: { on: '**61*{number}#', off: '##61#' },
      busy: { on: '**67*{number}#', off: '##67#' }, unreachable: { on: '**62*{number}#', off: '##62#' }, cancel_all: '##004#' },
    uscellular: { label: 'US Cellular', kind: 'cdma_style', number_format: 'national10', recommended: 'no_answer',
      all: { on: '*72{number}', off: '*720' }, no_answer: { on: '*92{number}', off: '*93' }, busy: { on: '*90{number}', off: '*91' },
      cancel_all: '*73' },
    generic_gsm: { label: 'Other GSM carrier', kind: 'gsm', number_format: 'digits11', recommended: 'no_answer',
      all: { on: '**21*{number}#', off: '##21#' }, no_answer: { on: '**61*{number}#', off: '##61#' },
      busy: { on: '**67*{number}#', off: '##67#' }, unreachable: { on: '**62*{number}#', off: '##62#' }, cancel_all: '##002#' },
  },
});

/**
 * Tenant SMS (P4, Telnyx only). Every message = SMS_LIMITS.prefix + body [+ stop footer on the first message to a number
 * from a sender]; rendered length <= SMS_LIMITS.maxChars with every var at SMS_VAR_MAX (check-contracts enforces it).
 * Placeholders are {var}; a [ ... ] segment is dropped unless every var inside it is present. Bodies are plain ASCII.
 * consent: 'required' = latest ops.contact_consents row must be yes; 'requested' = a one-off the recipient/employer asked
 * for (start code, tech invite). STOP is always enforced. Change a body → bump its version.
 */
export const SMS_LIMITS = /** @type {const} */ ({ maxChars: 320, prefix: '{business} via RNR OS: ', stopFooter: ' Reply STOP to opt out' });

export const SMS_VAR_MAX = /** @type {const} */ ({
  business: 40, first_name: 20, service: 40, when: 32, address: 60, callback: 17, tech: 20, eta: 16, vehicle: 40,
  amount: 12, link: 64, code: 8, job_no: 10, caller_name: 30, reason: 80, summary: 120, due: 24,
});

const T = (key, audience, vars, body, consent = 'required') => ({ key, version: '2026-09-27.1', audience, consent, vars, body });
export const SMS_TEMPLATES = Object.freeze({
  booking_confirmed: T('booking_confirmed', 'customer', ['first_name', 'service', 'when', 'address', 'callback'],
    "Hi {first_name}, you're booked for {service} on {when}.[ Address: {address}.][ To change it, call {callback}.]"),
  booking_reminder: T('booking_reminder', 'customer', ['service', 'when', 'address', 'callback'],
    'Reminder: {service} is scheduled for {when}.[ Address: {address}.][ To reschedule, call {callback}.]'),
  tech_on_the_way: T('tech_on_the_way', 'customer', ['tech', 'eta', 'link'],
    '{tech} is on the way[ and should arrive in about {eta}].[ Track: {link}]'),
  estimate_ready: T('estimate_ready', 'customer', ['first_name', 'vehicle', 'amount', 'link'],
    'Hi {first_name}, your estimate for {vehicle} is ready ({amount}). Review and approve: {link}'),
  invoice_ready: T('invoice_ready', 'customer', ['vehicle', 'amount', 'link'],
    'Your invoice for {vehicle} is ready: {amount}. Pay securely: {link}'),
  payment_receipt: T('payment_receipt', 'customer', ['amount', 'job_no', 'link'],
    'Payment received: {amount} for job {job_no}. Thank you![ Receipt: {link}]'),
  refund_issued: T('refund_issued', 'customer', ['amount', 'job_no'],
    'A refund of {amount} for job {job_no} is on its way. It can take 5-10 business days to show on your statement.'),
  payment_reminder: T('payment_reminder', 'customer', ['amount', 'job_no', 'link'],
    'Reminder: {amount} is due for job {job_no}. Pay securely: {link}'),
  tech_invite: T('tech_invite', 'tech', ['first_name', 'link'],
    "Hi {first_name}, you're invited to the RNR OS Tech app. Sign in with this phone number: {link}", 'requested'),
  start_code: T('start_code', 'customer', ['code'],
    'Your service start code is {code}. Give it to your technician only once they arrive.', 'requested'),
  login_code: T('login_code', 'tech', ['code'],                                        // P1 auth: staff/tech sign-in OTP
    'Your sign-in code is {code}. It expires in 15 minutes. Never share it.', 'requested'),
  verify_code: T('verify_code', 'customer', ['code'],                                  // P1 auth: customer step-up OTP
    'Your verification code is {code}. It expires in 15 minutes. Never share it.', 'requested'),
  missed_call_summary: T('missed_call_summary', 'staff', ['caller_name', 'callback', 'reason'],
    'Missed call from {caller_name} at {callback}.[ Re: {reason}.] Please call them back.'),
  voicemail_summary: T('voicemail_summary', 'staff', ['caller_name', 'callback', 'summary'],
    'New voicemail from {caller_name} at {callback}:[ {summary}]'),
  transfer_missed: T('transfer_missed', 'staff', ['caller_name', 'vehicle', 'reason', 'callback'],
    'Missed transfer: {caller_name}[, {vehicle}][, re: {reason}]. Please call back at {callback}.'),
  // P7 customer pages, passport, fleet portal, recap
  tech_assigned: T('tech_assigned', 'customer', ['tech', 'vehicle', 'link'],
    '{tech} is scheduled for your {vehicle} service.[ Track: {link}]'),
  review_request: T('review_request', 'customer', ['first_name', 'tech', 'link'],
    'Hi {first_name}, thanks for your visit[ with {tech}]. How did we do? {link}'),
  service_reminder: T('service_reminder', 'customer', ['service', 'vehicle', 'callback'],
    '{service} is coming due for your {vehicle}.[ To book, call {callback}.]'),
  portal_invite: T('portal_invite', 'customer', ['first_name', 'link'],
    'Hi {first_name}, here is your fleet portal sign-in link: {link}', 'requested'),
  morning_recap: T('morning_recap', 'staff', ['summary'],
    'Yesterday: {summary}', 'requested'),
  // Booking requests + callback escalation (booking_mode request / settings.handoff)
  booking_request: T('booking_request', 'staff', ['caller_name', 'vehicle', 'service', 'when', 'due', 'callback'],
    'Booking request: {caller_name}[, {vehicle}][, {service}][, prefers {when}]. Confirm by {due} on Lead Desk.[ {callback}]', 'requested'),
  callback_due: T('callback_due', 'staff', ['caller_name', 'callback', 'reason', 'due'],
    'Callback promised: {caller_name} at {callback}[ re: {reason}]. The AI told them the team will call back. Due by {due}.', 'requested'),
  callback_overdue: T('callback_overdue', 'staff', ['caller_name', 'callback', 'reason', 'due'],
    'Overdue: nobody has called {caller_name} back at {callback}[ re: {reason}]. It was due {due}. Call now or claim it on the Lead Desk.', 'requested'),
});

const SMS_ASCII = { '‘': "'", '’': "'", '“': '"', '”': '"', '–': '-', '—': '-', '…': '...', ' ': ' ' };
/** One var → plain single-line ASCII-ish text, capped at SMS_VAR_MAX. */
export function smsVar(name, v) {
  if (v == null) return '';
  const s = String(v).replace(/[‘’“”–—… ]/g, (c) => SMS_ASCII[c])
    .replace(/[\u0000-\u001F\u007F]+/g, ' ').replace(/[{}[\]]/g, '').replace(/\s+/g, ' ').trim();
  return s.slice(0, SMS_VAR_MAX[name] ?? 40).trim();
}

/**
 * @param {string} key @param {Record<string, unknown>} vars (must include business)
 * @param {{stopFooter?: boolean}} [opts]
 * @returns {{ok:true, text:string, version:string, template:object} | {ok:false, error:string, missing?:string[]}}
 */
export function renderSms(key, vars = {}, { stopFooter = false } = {}) {
  const t = SMS_TEMPLATES[key];
  if (!t) return { ok: false, error: 'template_unknown' };
  const val = (n) => smsVar(n, vars[n]);
  if (!val('business')) return { ok: false, error: 'missing_vars', missing: ['business'] };
  const optional = new Set();
  for (const seg of t.body.matchAll(/\[([^\]]*)\]/g)) for (const m of seg[1].matchAll(/\{(\w+)\}/g)) optional.add(m[1]);
  const missing = t.vars.filter((n) => !optional.has(n) && !val(n));
  if (missing.length) return { ok: false, error: 'missing_vars', missing };
  const body = t.body
    .replace(/\[([^\]]*)\]/g, (_, seg) => ([...seg.matchAll(/\{(\w+)\}/g)].every((m) => val(m[1])) ? seg : ''))
    .replace(/\{(\w+)\}/g, (_, n) => val(n));
  const text = SMS_LIMITS.prefix.replace('{business}', val('business')) + body + (stopFooter ? SMS_LIMITS.stopFooter : '');
  return { ok: true, text, version: t.version, template: t };
}

/**
 * Voice enums (P4). Mirrored by CHECK constraints in db/migrations/170_voice.sql (check-contracts compares them).
 * VOICE_LIMITS are the route()/click-to-call rules from P4_SCENARIOS (S16, S19, S28, S29, S44, S45, S53).
 */
export const VOICE_CALL_KINDS = /** @type {const} */ (['ai_inbound', 'click_to_call', 'masked', 'transfer', 'tech_check', 'voicemail']);
export const VOICE_OUTCOMES = /** @type {const} */ (['booked', 'requested', 'transferred', 'message', 'abandoned', 'spam', 'info_only']);
export const VOICE_ROUTE_ACTIONS = /** @type {const} */ (['ai', 'bridge_tech', 'transfer', 'voicemail', 'reject']);
export const VOICE_AI_MODES = /** @type {const} */ (['always', 'after_hours_only', 'overflow']);
export const VOICE_AFTER_HOURS_ACTIONS = /** @type {const} */ (['ai', 'voicemail']);
export const VOICE_RECORDING_LEGS = /** @type {const} */ (['mixed', 'a', 'b']);
export const VOICE_FORWARDING_MODES = /** @type {const} */ (['no_answer', 'busy', 'unconditional', 'unknown']);
export const VOICE_CALL_EVENT_TYPES = /** @type {const} */ (['disclosure_played', 'answered', 'bot_unavailable', 'transfer_started',
  'transfer_answered', 'transfer_failed', 'spam', 'forwarding_verified', 'voicemail', 'sms_consent', 'tech_check_outcome', 'completed']);
export const VOICE_TECH_CHECK_OUTCOMES = /** @type {const} */ (['on_my_way', 'delayed', 'cant_make_it', 'no_answer']);
export const VOICE_LIMITS = /** @type {const} */ ({
  maxConcurrentAiCalls: 20, spamThreshold: 3, spamWindowHours: 24, spamBlockHours: 24, duplicateBookingHours: 24,
  clickToCallLockSec: 30, proxyRingSec: 25, proxyGraceHours: 2, transferRingSec: 20, voicemailMaxSec: 120,
  recordingRetentionDays: 90, availabilityWindows: 5,
});

/**
 * The house sales line (voice/house.js, outreach/house-line.js; migration 372): our own US number, answered by our own
 * voice AI as the internal store `tenantId`, whose calls become a sales lead, a desk task and an email. On while
 * HOUSE_LINE_DID is set. `intents` is what the house conversation (voice_us/gpu/dialog_us/handlers/house_flow.py
 * INTENTS, compared by a test) reports as the reason for the call; `persona` is the tenant config's word for it.
 */
export const HOUSE_LINE = /** @type {const} */ ({
  tenantId: '5a1e5000-0000-4000-8000-000000000001',
  persona: 'house',
  intents: ['demo', 'pricing', 'support', 'returning_call', 'partnership', 'do_not_call', 'other', 'unclear'],
});

/**
 * US sales desk (api/src/modules/outreach). DESK_FEATURES are exactly the keys of GET /v1/desk/me `features`
 * (dial.js features(); a test compares them). The dashboard's flag list mirrors this.
 */
export const DESK_FEATURES = /** @type {const} */ (['click_to_call', 'training', 'recordings', 'softphone_webrtc', 'meetings', 'war_room', 'voice_test', 'voice_profiles', 'voice_eval', 'voice_samples', 'manual_leads', 'growth_os']);
/**
 * Desk call outcomes the database accepts (gtm.desk_calls.outcome CHECK, 231 → 279 → 291; check-contracts compares).
 * 'paylink_sent' and 'paid' are legacy (kept for old rows; the wrap-up no longer offers them). 'voicemail' only with
 * DESK_SEQUENCE_STEPS. pricing_sent → agreement_sent → signed is the dealership close (291).
 */
export const DESK_CALL_OUTCOMES = /** @type {const} */ (['no_answer', 'gatekeeper', 'voicemail', 'conversation', 'demo', 'pricing_sent',
  'agreement_sent', 'signed', 'paylink_sent', 'paid', 'not_interested', 'callback', 'wrong_number', 'dnc']);
/** A desk quote's life (gtm.desk_quotes.status, 286 → 291): sent directly, or needs_approval → approved | declined → sent once. */
export const DESK_QUOTE_STATUSES = /** @type {const} */ (['sending', 'sent', 'failed', 'needs_approval', 'approved', 'declined']);
/** What came of a demo meeting (app.demo_bookings.outcome, 292), set from the desk's Meetings tab. */
export const MEETING_OUTCOMES = /** @type {const} */ (['showed', 'no_show', 'cancelled_by_us']);
/** Showed / no-show may be marked from this many minutes before a meeting's start (a prospect who joins early). */
export const MEETING_EARLY_MINUTES = 10;
/**
 * Desk v2 (plans/P5_SALES_DASHBOARD.md). DESK_FLAG_FEATURES are the GET /v1/desk/me `features` keys that come from
 * core.feature_flags (shared/flags.mjs) rather than from the API's settings, so they stay apart from DESK_FEATURES.
 */
export const DESK_FLAG_FEATURES = /** @type {const} */ (['desk_v2', 'coaching_ui', 'desk_qualification_card']);
/**
 * The desk status of a lead (shared/desk_status.mjs; gtm.desk_status() in 366 is its SQL mirror and a test runs the
 * same fixtures through both). Derived from events, never typed: precedence dnc > live > paid > signed > lost > nurture
 * > agreement_sent > pricing_sent > demo_done > demo_booked > the last call's outcome > new.
 */
export const DESK_STATUSES = /** @type {const} */ (['new', 'attempted', 'voicemail', 'connected', 'conversation', 'callback',
  'not_interested', 'wrong_number', 'paylink_sent', 'demo_booked', 'demo_confirmed', 'demo_done', 'qualified', 'pricing_sent',
  'agreement_sent', 'signed', 'paid', 'live', 'nurture', 'lost', 'dnc']);
/**
 * Tasks the desk creates by itself (outreach/desk-tasks.js; gtm.tasks.trigger). gtm.tasks.trigger has no CHECK (many
 * modules write their own triggers), so this list is the desk's own vocabulary, not a database rule.
 */
export const DESK_TASK_TRIGGERS = /** @type {const} */ (['inbound_lead', 'follow_up', 'pricing_follow_up', 'agreement_chase',
  'meeting_outcome_due', 'exit_call', 'onboarding_call', 'meeting_link_missing', 'demo_confirm', 'lifecycle_call',
  // growth OS DEMO-04 (a Meet room that would not record by itself; no recording 2 hours after a demo; nobody joined at
  // T+3) and SALES-07 (the referral ask when a store pays and 30 days later)
  'recording_manual', 'recording_missing', 'demo_nobody_joined', 'demo_host_absent', 'referral_ask']);
/** Who put a task on someone's list (gtm.tasks.source CHECK in 360; check-contracts compares). */
export const DESK_TASK_SOURCES = /** @type {const} */ (['system', 'manager', 'rep']);
/**
 * The qualification card's attendee titles (gtm.desk_qualification.attendee_title CHECK in 393; check-contracts compares)
 * and the ones that are a decision-maker (growth OS v2.1 §3.3).
 */
export const DESK_QUAL_TITLES = /** @type {const} */ (['dealer_principal', 'owner', 'gm', 'gsm', 'fixed_ops_director', 'service_director',
  'service_manager', 'bdc_manager', 'service_advisor', 'other']);
export const DESK_DM_TITLES = /** @type {const} */ (['dealer_principal', 'owner', 'gm', 'gsm', 'fixed_ops_director', 'service_director',
  'service_manager', 'bdc_manager']);
/** A demo's recording (app.demo_bookings.recording_state CHECK in 392): none → requested | missing → recording_seen → ingested. */
export const DEMO_RECORDING_STATES = /** @type {const} */ (['none', 'requested', 'recording_seen', 'ingested', 'missing', 'declined', 'purged']);
/** Who wrote a transcript (gtm.transcripts.engine CHECK in 392): Meet's own, or our worker rnr_us_stt's model. */
export const TRANSCRIPT_ENGINES = /** @type {const} */ (['meet', 'whisper-large-v3-turbo', 'whisper-small.en']);
/**
 * The rules scorecard, version 1 (api/src/modules/coaching/score.js): each part's weight out of 100. A part that cannot be
 * measured (talk ratio on a mono call) is left out and the others are scaled up. Labelled "rules v1"; no pay uses it.
 */
export const SCORECARD_WEIGHTS_V1 = Object.freeze({ talk_ratio: 20, questions: 20, discovery: 20, next_step: 20, disclosure: 10, monologue: 10 });
/** Activation of a paying store (growth OS v2.1 §6.7): live within 7 days of payment, activated at 10 real calls in 14 days. */
export const ACTIVATION = Object.freeze({ live_within_days: 7, real_calls: 10, window_days: 14 });
/**
 * Churn signals of a live store (gtm.v_store_churn_signals, 394), each a rule over that view's row: calls down more than
 * half week over week (from at least 10 the week before), no forwarded call for 3 days after having some, nobody from the
 * store signed in for 7 days, a failed payment.
 */
export const CHURN_RULES = Object.freeze({ calls_drop_pct: 50, calls_drop_min_prev: 10, no_forward_days: 3, no_login_days: 7 });
/**
 * Desk permissions (growth OS ACC-03): every /v1/desk route names exactly one (core/auth/guards.js requireDesk({perm})),
 * and a route-table test fails on a route without one. Row scoping (whose calls, whose demos) stays in the services.
 */
export const DESK_PERMS = /** @type {const} */ ([
  'desk.use', 'library.read', 'leads.read', 'leads.write', 'leads.claim', 'calls.dial', 'emails.send', 'paylinks.send',
  'quotes.send', 'agreements.send', 'stores.work', 'training', 'voice_test', 'today.own', 'recordings.own',
  'demos.book', 'demos.booked_by_me', 'demos.outcome',
  'demos.all', 'demos.host', 'recordings.leads_i_own', 'pipeline.own', 'availability.own', 'demo_media.all',
  'recordings.all', 'recordings.review', 'team.read', 'team.assign', 'quotes.approve', 'grants.approve', 'stores.manage',
  'voice_test.review', 'voice_test.spend', 'analytics.web', 'analytics.funnel', 'hosts.manage', 'availability.manage', 'today.team',
  // WP_E VOICE-5/8: queue or cancel a voice bake-off run and render previews (voice_eval.run); approve, publish and retire a
  // voice sample (voice_samples.approve). Managers and the owner; reps see the runs and the library and make drafts (voice_test).
  'voice_eval.run', 'voice_samples.approve',
  'admin',
  // growth OS (api/src/modules/growth, WP_D): reps see the alert feed and People; managers the War room, the Funnel, data
  // health, rules, team and digest; changing a rule and the cost ledger are the owner's (admin) only. growth.ads: Marketing ▸
  // Ads (/v1/desk/meta/*, /v1/desk/growth/ads; WP_B_META), managers
  'growth.alerts', 'growth.people', 'growth.summary', 'growth.funnel', 'growth.health', 'growth.rules.read', 'growth.team',
  'growth.digest', 'growth.rules.write', 'growth.costs', 'growth.ads',
]);
const DESK_BASE = ['desk.use', 'library.read', 'leads.read', 'leads.write', 'leads.claim', 'calls.dial', 'emails.send',
  'paylinks.send', 'quotes.send', 'agreements.send', 'stores.work', 'training', 'voice_test', 'today.own', 'recordings.own',
  // demos.outcome: a desk rep still marks the demos it is assigned (meetings.js checks the assignee) until a closer runs them
  'demos.book', 'demos.booked_by_me', 'demos.outcome', 'growth.alerts', 'growth.people'];
const CLOSER = [...DESK_BASE, 'demos.all', 'demos.host', 'recordings.leads_i_own', 'pipeline.own', 'availability.own', 'demo_media.all'];
const MANAGER = [...CLOSER, 'recordings.all', 'recordings.review', 'team.read', 'team.assign', 'quotes.approve', 'grants.approve',
  'stores.manage', 'voice_test.review', 'voice_test.spend', 'voice_eval.run', 'voice_samples.approve', 'analytics.web', 'analytics.funnel',
  'hosts.manage', 'availability.manage', 'today.team',
  'growth.summary', 'growth.funnel', 'growth.health', 'growth.rules.read', 'growth.team', 'growth.digest', 'growth.ads'];
/**
 * Desk role → its permissions. admin = every permission ('*'). The closer is not a platform role: it is a desk account whose
 * US profile says so (gtm.desk_reps.function = 'closer'; guards.js deskRoleOf); a manager or admin stays what it is.
 */
export const DESK_ROLE_PERMS = Object.freeze({
  desk: Object.freeze(DESK_BASE),
  closer: Object.freeze(CLOSER),
  desk_manager: Object.freeze(MANAGER),
  admin: '*',
});
/** The permissions of a desk role as a Set (admin: all of DESK_PERMS; an unknown role: none). */
export function deskPermsOf(role) {
  const p = DESK_ROLE_PERMS[role];
  return new Set(p === '*' ? DESK_PERMS : p || []);
}
/** How the rep's leg of a desk call is reached, and the recording rule a call ran under (CHECKs in 273; check-contracts compares). */
export const DESK_REP_MODES = /** @type {const} */ (['mobile', 'browser']);
export const DESK_RECORDING_MODES = /** @type {const} */ (['notice_then_record', 'no_record']);
/**
 * Desk rep phones (the ring-a-phone click-to-call). A rep's phone COUNTRY is an explicit profile field
 * (gtm.desk_rep_phones.country_code: '+91' by default, or '+1'), set per rep by a manager; it is never inferred from the
 * digits of a number. The stored number is E.164 and must agree with the field (CHECK in 275). The rep leg is routed by
 * the field: +91 → the India rep trunk (as always), +1 → the US carrier.
 */
export const DESK_REP_COUNTRY_CODES = /** @type {const} */ (['+91', '+1']);
export const DESK_REP_DEFAULT_COUNTRY_CODE = '+91';
export const DESK_REP_ROUTES = /** @type {const} */ ({ '+91': 'india_gateway', '+1': 'us_carrier' });
const REP_E164 = { '+1': /^\+1[2-9]\d{2}[2-9]\d{6}$/, '+91': /^\+91[6-9]\d{9}$/ };

/** The country code an E.164 rep number carries: '+1' | '+91' | null (another country, or not a valid number of either). */
export function repCountryCode(e164) {
  const s = String(e164 ?? '');
  return REP_E164['+1'].test(s) ? '+1' : REP_E164['+91'].test(s) ? '+91' : null;
}

/** A number written with a leading '+': the country code it names when that is one of ours, else null. */
export function explicitRepCountry(raw) {
  const s = String(raw ?? '').trim();
  if (!s.startsWith('+')) return null;
  const d = s.replace(/\D/g, '');
  return d.startsWith('91') ? '+91' : d.startsWith('1') ? '+1' : null;
}

/**
 * The 10-digit national number of `raw` FOR the given country, or null. Accepts the bare national number, or the number
 * written with THAT country's code ('+91…' / '91' + 10 digits / the Indian trunk '0' + 10 digits; '+1…' / '1' + 10 digits).
 * A number written with any other country's code is null: the country is the field, never the digits.
 */
export function repNational(raw, countryCode) {
  if (!DESK_REP_COUNTRY_CODES.includes(countryCode)) return null;
  const s = String(raw ?? '').trim();
  const d = s.replace(/\D/g, '');
  const cc = countryCode.slice(1);
  let n = null;
  if (s.startsWith('+')) n = d.startsWith(cc) ? d.slice(cc.length) : null;
  else if (d.length === 10) n = d;
  else if (d.length === 10 + cc.length && d.startsWith(cc)) n = d.slice(cc.length);
  else if (countryCode === '+91' && d.length === 11 && d.startsWith('0')) n = d.slice(1);
  return n && n.length === 10 && REP_E164[countryCode].test(`${countryCode}${n}`) ? n : null;
}

/** country code + a number in any accepted shape → the E.164 number, or null. */
export function repE164(countryCode, raw) {
  const n = repNational(raw, countryCode);
  return n ? `${countryCode}${n}` : null;
}

/** Inbound desk lines and calls (CHECKs in 274; check-contracts compares). 'ringing', 'voicemail', 'missed' are inbound-only. */
export const DESK_DIAL_STATUSES = /** @type {const} */ (['dialing_rep', 'rep_answered', 'dialing_lead', 'connected', 'ended', 'failed', 'ringing', 'voicemail', 'missed']);
export const DESK_LINE_KINDS = /** @type {const} */ (['sales', 'after_hours']);
export const DESK_LINE_RECORDING_MODES = /** @type {const} */ (['notice_then_record', 'no_record', 'by_caller_state']);
export const DESK_VOICEMAIL_STATUSES = /** @type {const} */ (['none', 'recorded', 'empty', 'failed_storage']);
/** California area codes: a number in one of these is treated as a California number whatever state the lead lists. */
export const CA_AREA_CODES = /** @type {const} */ (['209', '213', '279', '310', '323', '341', '350', '369', '408', '415', '424', '442', '510', '530',
  '559', '562', '619', '626', '628', '650', '657', '661', '669', '707', '714', '747', '760', '805', '818', '820', '831', '840', '858', '909',
  '916', '925', '949', '951']);

/**
 * Booking authority (tenant setting `settings.booking_mode`, P1 PATCH /v1/app/tenant; chosen in onboarding). The AI only
 * promises what the mode can keep:
 *   rnr_schedule       books real windows from the tenant's hours/schedule in RNR OS (Solo, Mobile Ops, Fleet Ops, Command Center)
 *   request            takes a preferred time; staff confirm it from the Lead Desk (lead status `requested`, SLA timer)
 *                      and the customer's confirmation text goes out then (Front Office: calendar lives in the shop system)
 *   reserved_capacity  books only inside blocks the shop reserved for RNR (`settings.reserved_blocks`); full → request
 * External shop-system write-back (Tekmetric / Shopmonkey) is not a mode: it is rolling out.
 */
export const BOOKING_MODES = /** @type {const} */ (['rnr_schedule', 'request', 'reserved_capacity']);
/** Plans whose products include the tech app run their schedule in RNR OS → rnr_schedule; everyone else → request. */
export const BOOKING_MODE_SCHEDULE_PRODUCT = 'tech_app';
/** Segment fallback when the plan is unknown. */
export const BOOKING_MODE_BY_SEGMENT = /** @type {const} */ ({
  mobile_mechanic: 'rnr_schedule', fleet: 'rnr_schedule', roadside: 'rnr_schedule', repair_shop: 'request', franchise: 'request',
  dealership: 'request',   // every booking is made in the store's own scheduler
});

/**
 * Handoffs & exception ownership (tenant setting `settings.handoff`). A caller who asks for a person is transferred live
 * only when `transfer_when` allows it now AND an on-call number (voice transfer_rules) applies; otherwise the AI promises
 * a callback and the lead gets `respond_by` (business hours: now + callback_minutes; closed: next opening +
 * callback_minutes). Unanswered by the deadline → SMS to the backup owner, then the owner (every
 * escalation_step_minutes), then the lead is marked overdue (Lead Desk + morning recap).
 */
export const HANDOFF_TRANSFER_WHEN = /** @type {const} */ (['business_hours', 'always', 'never']);
export const HANDOFF_DEFAULTS = /** @type {const} */ ({
  transfer_when: 'business_hours', primary_user_id: null, backup_user_id: null, callback_minutes: 15, escalation_step_minutes: 10,
});
/** AI lead reasons that need no human response (no deadline): the caller got their answer or was not a customer. */
export const LEAD_NO_CALLBACK_REASONS = /** @type {const} */ (['info_only', 'price_only', 'out_of_area', 'spam', 'wrong_number']);
